import { useConfigStore } from '@/stores';
import styles from './styles.module.scss';
import clsx from 'clsx';
import { useOrientation, useScreenSize, useTickSoundInteraction } from '@/hooks';
import type { ListTypeListItem } from '@/types/list-type';
import { memo, useCallback } from 'react';
import { getS3UploadUrl, isS3UploadUrl } from '@/utils/s3Config';
import QRCode from 'react-qr-code';
import PlaceHolder from '@/components/icons/shop/PlaceHolder';
import { useTranslation } from 'react-i18next';
import { KEY_CODE_SIHEUNG } from '@/constants/app';

interface EventCarouselItemProps {
    content: ListTypeListItem;
    tabIndex: number;
    onClick?: (data: ListTypeListItem) => void;
}

const EventCarouselItem = memo(({ content, tabIndex, onClick }: EventCarouselItemProps) => {
    const { t } = useTranslation();

    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const storeCode = useConfigStore((state) => state.config.server?.store?.storeCode) ?? '';
    const { isHorizontal } = useOrientation();
    const isSiheungVertical = KEY_CODE_SIHEUNG.includes(storeCode) && !isHorizontal;
    const { handleInteraction, handleKeyboardInteraction } = useTickSoundInteraction();
    const { scaleHeightPixel } = useScreenSize();

    const url = content?.contentsUrl?.url;
    const urlPreview: string | null | undefined = url ? (isS3UploadUrl(url) ? url : getS3UploadUrl(url)) : '';

    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            const parent = e.currentTarget;
            parent.classList.add('poi-active');
            handleInteraction(e, () => onClick?.(content));
        },
        [handleInteraction, onClick, content]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => handleKeyboardInteraction(e, () => onClick?.(content)),
        [handleKeyboardInteraction, onClick, content]
    );

    const ariaLabel = `${content.title}`;
    const qrCode = content.qrConnect && (
        <div
            className={clsx(styles['wrapper__qr'], {
                [styles['wrapper__qr__halfmode']]: isHorizontal && screenMode === 'split',
                [styles['wrapper__qr__siheung-vertical']]: isSiheungVertical,
            })}
            aria-hidden={isSiheungVertical || undefined}
        >
            <QRCode value={content.qrUrl} size={scaleHeightPixel(isSiheungVertical ? 60 : 72)} />
        </div>
    );

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
            key={content.id}
            tabIndex={tabIndex}
            data-interactive
            data-playsound="ting"
            className={clsx(styles.wrapper, {
                [styles['wrapper__contrast']]: contrast,
                [styles['wrapper__vertical']]: !isHorizontal,
                [styles['wrapper__vertical--halfmode']]: !isHorizontal && screenMode === 'split',
                [styles['wrapper__horizontal--halfmode']]: isHorizontal && screenMode === 'split',
            })}
            aria-label={ariaLabel}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            data-role={t('common.button.tts.popup_button')}
        >
            <div
                className={clsx(styles['wrapper__image'], {
                    [styles['wrapper__image__vertical']]: !isHorizontal,
                    [styles['wrapper__image__horizontal--halfmode']]: isHorizontal && screenMode === 'split',
                    [styles['wrapper__image__siheung-vertical']]: isSiheungVertical,
                })}
            >
                {urlPreview ? (
                    <img src={urlPreview} alt={isSiheungVertical ? '' : 'img error'} loading="lazy" />
                ) : (
                    <PlaceHolder />
                )}
            </div>

            {isSiheungVertical ? (
                <div className={styles['wrapper__footer']}>
                    <p className={styles['wrapper__title']}>{content.title}</p>
                    {qrCode}
                </div>
            ) : (
                qrCode
            )}
        </div>
    );
});

EventCarouselItem.displayName = 'EventCarouselItem';

export default EventCarouselItem;
